import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HiOutlineTag,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineSparkles,
} from 'react-icons/hi';
import {
  setDiscountCode,
  setBasketPrices,
  removeItem,
} from '../../../../store/basket/basketSlice';
import { apiCalculateBasket } from '../../../../services/OrderService';
import { apiGetAvailableCoupons } from '../../../../services/CouponService';
import { buildCalculateBasketPayload } from '../../../../utils/basketCalculatePayload';
import {
  readCouponOutcome,
  couponLines,
  findCampaignByCode,
  belowMinimumSpend,
  minimumSpendSentence,
} from '../../../../utils/coupon';
import useCouponHandler from '../../../../utils/hooks/useCouponHandler';
import ProductModal from '../../../items/Product/ProductModal';
import toast from '../../../ui/toast/toast';
import Notification from '../../../ui/Notification';

const INVALID_CODE = "That code isn't valid for this restaurant.";
const SOMETHING_WRONG = 'Something went wrong. Please try again.';

// PublicCoupon/GetAvailable answers in one of three shapes (as read in
// CouponSlider). A failed request resolves undefined (BaseService), and an
// answer with no list is as good as failed: null, not an empty list.
const campaignList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return null;
};

/**
 * Premium "Apply coupon" modal.
 * - Clear hero with coupon icon, title, subtitle.
 * - Refined input with accent focus ring + inline Apply button.
 * - Applied state with green check + Clear action.
 * - Subtle savings hint under the input.
 */
const CouponModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const store = useSelector((state) => state.base.common.currentStore);
  // No menu argument: the handler's item and product lookups fall back to
  // currentStore.menus, every menu of the store.
  const { handleApplyCoupon } = useCouponHandler(null);
  const [code, setCode] = useState(basket.discountcode || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // A typed type 0 / type 2 campaign opens its product modal from here, the
  // way a tapped card does in CouponSlider. This modal stays open under it:
  // closing its Dialog would unmount the product modal too.
  const [bxgyModal, setBxgyModal] = useState(null);
  const [freeItemModal, setFreeItemModal] = useState(null);

  useEffect(() => {
    setCode(basket.discountcode || '');
    setError('');
  }, [basket.discountcode]);

  const applied = !!basket.discountcode;
  const canApply = code.trim().length > 0 && !submitting;
  // The figure CouponCard's lock reads (CouponSlider.jsx).
  const subtotal =
    basket?.prices?.subtotal ??
    basket?.items?.reduce((sum, i) => sum + (i.total || 0), 0) ??
    0;

  // Spec 2026-10-21 §3, the kiosk pattern. A typed code is first looked up,
  // case-insensitively, in the store's campaign list and applied like a
  // tapped card, with the campaign's own code. Anything else is priced by
  // Discount/CalculateForCustomer, which says whether it took. IsCouponApply
  // is not asked: it knows only legacy codes and no basket.
  const handleApply = async (e) => {
    e?.preventDefault?.();
    if (!canApply) return;
    const typed = code.trim();
    setSubmitting(true);
    setError('');
    try {
      const listResponse = await apiGetAvailableCoupons(store?.id, 10);
      const campaigns = campaignList(listResponse);
      // A failed list read says so and sends nothing to Calculate: for a
      // type 0 campaign the API adds the free unit itself and answers
      // applied, and the box would say "applied" with no free line in the
      // cart. A list that was read (empty or not) takes today's path.
      if (campaigns === null) {
        setError(SOMETHING_WRONG);
        return;
      }
      const campaign = findCampaignByCode(campaigns, typed);
      if (campaign) {
        // A card is locked below its minimum spend; so is its typed code.
        // The code is valid, so the customer reads what is missing (the
        // payment page's line), and nothing goes in the basket.
        if (belowMinimumSpend(campaign, subtotal)) {
          setError(minimumSpendSentence(campaign, subtotal));
          return;
        }
        const result = await handleApplyCoupon(campaign);
        if (result?.type === 'BXGY_SELECTION_REQUIRED') {
          setBxgyModal({ product: result.product, coupon: result.coupon });
        } else if (result?.type === 'FREE_ITEM_SELECTION_REQUIRED') {
          setFreeItemModal({
            product: result.product,
            coupon: result.coupon,
            lockedItemId: result.lockedItemId,
          });
        } else {
          onClose?.();
        }
        return;
      }

      const response = await apiCalculateBasket({
        ...buildCalculateBasketPayload(basket, store?.id),
        discountcode: typed,
      });
      const prices = response?.data;
      // A failed request resolves undefined (BaseService): the basket was not
      // priced, which is not the same as the code being refused.
      if (!prices) {
        setError(SOMETHING_WRONG);
        return;
      }
      const outcome = readCouponOutcome(prices);
      // An older API sends no `coupon`; then a discount says it took (the
      // kiosk's rule).
      const took = outcome ? outcome.applied : prices?.discountprice > 0;
      if (took) {
        // The server's spelling of the code (spec §1.1), not the typed one.
        dispatch(setDiscountCode(outcome?.code || typed));
        dispatch(setBasketPrices(prices));
        toast.push(
          <Notification title="Coupon applied" type="success" duration={2000} />,
          { placement: 'top-center' },
        );
        onClose?.();
      } else {
        setError(outcome?.reason || INVALID_CODE);
      }
    } catch {
      setError(SOMETHING_WRONG);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    // The lines this code added (a free item, a deal line) go with it: left
    // behind they would show $0 and be charged as ordinary items on the next
    // recalculation. Only lines stamped with the current code are taken; a
    // paid line of the same item is never stamped.
    couponLines(basket.items, basket.discountcode).forEach((item) =>
      dispatch(removeItem(item.basketId)),
    );
    dispatch(setDiscountCode(''));
    setCode('');
    setError('');
    toast.push(
      <Notification title="Coupon removed" type="info" duration={2000} />,
      { placement: 'top-center' },
    );
  };

  return (
    <div
      className="w-full max-w-[420px] mx-auto relative"
      style={{
        padding: '28px 26px',
        fontFamily: 'Geist, system-ui, sans-serif',
      }}
    >
      <style>{`
        @keyframes cmFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cm-fade { animation: cmFadeUp 0.42s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .cm-input:focus-within {
          border-color: var(--ui-ink) !important;
          box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>

      {/* Icon hero */}
      <div
        className="cm-fade grid place-items-center mx-auto mb-4"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '14px',
          background: applied
            ? 'rgba(21, 128, 61, 0.12)'
            : 'var(--accent-soft, var(--site-color-10))',
          color: applied ? 'var(--ui-green)' : 'var(--accent, var(--site-color))',
          border: '1px solid var(--ui-line)',
        }}
      >
        {applied ? (
          <HiOutlineCheck size={24} strokeWidth={2.4} />
        ) : (
          <HiOutlineTag size={22} />
        )}
      </div>

      <h2
        className="cm-fade text-center"
        style={{
          animationDelay: '0.04s',
          fontSize: '22px',
          fontWeight: 800,
          letterSpacing: '-0.025em',
          color: 'var(--ui-ink)',
          lineHeight: 1.15,
          marginBottom: '6px',
        }}
      >
        {applied ? 'Coupon applied' : 'Apply a coupon'}
      </h2>
      <p
        className="cm-fade text-center mb-5"
        style={{
          animationDelay: '0.08s',
          fontSize: '13.5px',
          color: 'var(--ui-ink-2)',
          lineHeight: 1.5,
        }}
      >
        {applied
          ? 'Nice — your savings are already in the basket.'
          : 'Enter your code below. Savings update at checkout.'}
      </p>

      {/* Applied vs. input state */}
      {applied ? (
        <div
          className="cm-fade flex items-center justify-between"
          style={{
            animationDelay: '0.1s',
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'rgba(21, 128, 61, 0.06)',
            border: '1px solid rgba(21, 128, 61, 0.25)',
            marginBottom: '14px',
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <HiOutlineCheck
              size={16}
              style={{ color: 'var(--ui-green)', flexShrink: 0 }}
            />
            <span
              className="truncate"
              style={{
                fontFamily: 'Geist Mono, monospace',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                color: 'var(--ui-ink)',
              }}
            >
              {basket.discountcode}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-[4px] transition-colors"
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              fontWeight: 700,
              borderRadius: '8px',
              background: 'transparent',
              color: 'var(--ui-ink-2)',
              border: '1px solid var(--ui-line-2)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <HiOutlineX size={12} />
            Remove
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleApply}
          className="cm-fade"
          style={{ animationDelay: '0.1s' }}
        >
          <div
            className="cm-input flex items-center overflow-hidden mb-[8px]"
            style={{
              padding: '3px',
              borderRadius: '12px',
              background: 'var(--ui-surface)',
              border: `1px solid ${error ? '#DC2626' : 'var(--ui-line-2)'}`,
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
          >
            <HiOutlineTag
              size={15}
              style={{
                color: 'var(--ui-ink-3)',
                marginLeft: '10px',
                flexShrink: 0,
              }}
            />
            <input
              type="text"
              autoComplete="off"
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                if (error) setError('');
              }}
              placeholder="Enter coupon code"
              className="flex-1 outline-none min-w-0"
              style={{
                padding: '10px 12px',
                background: 'transparent',
                border: 'none',
                fontFamily: 'Geist Mono, monospace',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: 'var(--ui-ink)',
                textTransform: 'uppercase',
              }}
            />
            <button
              type="submit"
              disabled={!canApply}
              className="font-bold transition-transform"
              style={{
                padding: '0 14px',
                height: '36px',
                fontSize: '12.5px',
                borderRadius: '9px',
                background: canApply
                  ? 'var(--accent, var(--site-color))'
                  : 'var(--ui-surface-2)',
                color: canApply
                  ? 'var(--accent-fg, #FFFFFF)'
                  : 'var(--ui-ink-3)',
                border: 'none',
                cursor: canApply ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                margin: '0 2px',
              }}
            >
              {submitting ? 'Checking…' : 'Apply'}
            </button>
          </div>
          {error ? (
            <div
              style={{
                fontSize: '12px',
                color: '#B91C1C',
                fontWeight: 500,
                marginTop: '4px',
              }}
            >
              {error}
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-[4px]"
              style={{
                fontSize: '11.5px',
                color: 'var(--ui-ink-3)',
                fontWeight: 500,
              }}
            >
              <HiOutlineSparkles size={11} />
              Codes are case-insensitive
            </div>
          )}
        </form>
      )}

      {bxgyModal && (
        <ProductModal
          isOpen={!!bxgyModal}
          onClose={() => setBxgyModal(null)}
          product={bxgyModal.product}
          coupon={bxgyModal.coupon}
          onAddBXGYItem={async (selection) => {
            await handleApplyCoupon(bxgyModal.coupon, selection);
            setBxgyModal(null);
            onClose?.();
          }}
        />
      )}
      {freeItemModal && (
        <ProductModal
          isOpen={!!freeItemModal}
          onClose={() => setFreeItemModal(null)}
          product={freeItemModal.product}
          lockedItemId={freeItemModal.lockedItemId}
          onAddFreeItem={async (selection) => {
            await handleApplyCoupon(freeItemModal.coupon, selection);
            setFreeItemModal(null);
            onClose?.();
          }}
        />
      )}
    </div>
  );
};

export default CouponModal;
